import { Heart, CheckCircle2, Calendar, Building2 } from 'lucide-react';
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, collection, query, orderBy, getDocs, deleteDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import UserDashboardSidebar from './UserDashboardSidebar';
import './UserFavorites.css';

// Local Storage Helper
const LocalStorageHelper = {
  saveLoginData: (userId, email, name, userType = 'user') => {
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('userId', userId);
    localStorage.setItem('userEmail', email);
    localStorage.setItem('userName', name);
    localStorage.setItem('userType', userType);
  },

  isLoggedIn: () => {
    return localStorage.getItem('isLoggedIn') === 'true';
  },

  getLoginData: () => {
    return {
      userId: localStorage.getItem('userId'),
      email: localStorage.getItem('userEmail'),
      name: localStorage.getItem('userName'),
      userType: localStorage.getItem('userType'),
    };
  },

  clearLoginData: () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('userType');
  }
};

const UserFavorites = () => {
  const navigate = useNavigate();
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Authentication state
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userName, setUserName] = useState('User');
  const [profilePhoto, setProfilePhoto] = useState(null);

  // Favorites state
  const [favoritePhotographers, setFavoritePhotographers] = useState([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(true);
  const [favoriteStats, setFavoriteStats] = useState({
    total: 0,
    thisMonth: 0,
    categories: {}
  });

  // Authentication check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        await loadUserData(user.uid);
        await loadUserFavorites(user.uid);
      } else {
        navigate('/Login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load user data to get name and profile photo
  const loadUserData = async (userId) => {
    try {
      // Try users collection first
      let userDoc = await getDoc(doc(db, 'users', userId));
      
      if (!userDoc.exists()) {
        // Try photographers collection if not found in users
        userDoc = await getDoc(doc(db, 'photographers', userId));
      }
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        setUserName(data.name || data.displayName || 'User');
        setProfilePhoto(data.profileImage || data.avatar || null);
      } else {
        // Fallback to auth user data
        if (auth.currentUser) {
          setUserName(auth.currentUser.displayName || 'User');
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      // Fallback to auth user data
      if (auth.currentUser) {
        setUserName(auth.currentUser.displayName || 'User');
      }
    }
  };

  // Load user's favorite photographers
  const loadUserFavorites = async (userId) => {
    try {
      setIsLoadingFavorites(true);
      console.log('Loading favorites for user:', userId);

      const favoritesQuery = query(
        collection(db, 'users', userId, 'favorites'),
        orderBy('favoritedAt', 'desc')
      );
      
      const favoritesSnapshot = await getDocs(favoritesQuery);
      const favorites = [];
      const categories = {};
      let thisMonthCount = 0;
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      
      favoritesSnapshot.forEach((doc) => {
        const favoriteData = {
          id: doc.id,
          ...doc.data()
        };
        
        favorites.push(favoriteData);
        
        // Count categories
        const category = favoriteData.specialization || 'Photography';
        categories[category] = (categories[category] || 0) + 1;
        
        // Count this month's favorites
        if (favoriteData.favoritedAt) {
          const favoriteDate = favoriteData.favoritedAt.toDate();
          if (favoriteDate.getMonth() === currentMonth && favoriteDate.getFullYear() === currentYear) {
            thisMonthCount++;
          }
        }
      });
      
      setFavoritePhotographers(favorites);
      setFavoriteStats({
        total: favorites.length,
        thisMonth: thisMonthCount,
        categories: categories
      });
      
      console.log('Loaded favorites:', favorites);
      
    } catch (error) {
      console.error('Error loading favorites:', error);
      toast.error('Error loading favorites');
    } finally {
      setIsLoadingFavorites(false);
    }
  };

  // Remove photographer from favorites
  const removeFromFavorites = async (photographerId, photographerName) => {
    try {
      if (!currentUser) return;
      
      const favoriteDocRef = doc(db, 'users', currentUser.uid, 'favorites', photographerId);
      await deleteDoc(favoriteDocRef);
      
      // Update local state
      setFavoritePhotographers(prev => prev.filter(fav => fav.id !== photographerId));
      setFavoriteStats(prev => ({
        ...prev,
        total: prev.total - 1
      }));
      
      toast.success(`${photographerName} removed from favorites`);
      
    } catch (error) {
      console.error('Error removing from favorites:', error);
      toast.error('Error removing from favorites');
    }
  };

  // Navigate to photographer profile
  const viewPhotographerProfile = (photographerId) => {
    navigate(`/photographer/${photographerId}`);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      LocalStorageHelper.clearLoginData();
      navigate('/Login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Handle home navigation
  const handleHomeNavigation = () => {
    navigate('/LandingPage');
    closeSidebar();
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile && !sidebarVisible) {
        setSidebarVisible(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarVisible]);

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  };

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const date = timestamp.toDate();
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    
    return date.toLocaleDateString();
  };

  // FIXED: Render star rating with proper type conversion
  const renderStars = (rating) => {
    const numericRating = parseFloat(rating) || 0; // Convert to number
    const fullStars = Math.floor(numericRating);
    const hasHalfStar = numericRating % 1 !== 0;
    
    return Array.from({ length: 5 }, (_, index) => {
      if (index < fullStars) {
        return <span key={index} className="userbookings-favorite-star filled">★</span>;
      } else if (index === fullStars && hasHalfStar) {
        return <span key={index} className="userbookings-favorite-star half-filled">★</span>;
      } else {
        return <span key={index} className="userbookings-favorite-star">★</span>;
      }
    });
  };

  return (
    <div className="userbookings-app-container">
      <UserDashboardSidebar
        activeItem="favorites"
        visible={sidebarVisible}
        isMobile={isMobile}
        onToggle={toggleSidebar}
        onClose={closeSidebar}
        userName={userName}
        profilePhoto={profilePhoto}
        onHome={handleHomeNavigation}
        onLogout={handleLogout}
      />

      {/* Main Content */}
      <main className={`userbookings-main-content ${
        !sidebarVisible && !isMobile ? 'expanded' : ''
      }`}>
        <header className="userbookings-main-header">
          <div className="userbookings-welcome-container">
            <div className="userbookings-avatar-container">
              <div className="userbookings-user-avatar-main">
                {profilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt="Profile"
                    className="userbookings-user-avatar-main-img"
                  />
                ) : (
                  userName.charAt(0).toUpperCase()
                )}
              </div>
            </div>
            <div className="userbookings-welcome-text-container">
              <h1 className="userbookings-welcome-heading">
                Your Favorites<span className="userbookings-username-text">, {userName}</span>
                <span className="userbookings-wave-emoji"></span>
              </h1>
            </div>
          </div>
        </header>
        
        {/* Favorites Section with Browse Photographer Card Style */}
        <section className="userbookings-bookings-section">
          <h2 className="userbookings-section-title">
            My Favorite Photographers
          </h2>
          
          {isLoadingFavorites ? (
            <div className="userbookings-loading-favorites">
              <div className="userbookings-loading-spinner"></div>
              <p>Loading your favorites...</p>
            </div>
          ) : favoritePhotographers.length > 0 ? (
            <div className="userbookings-favorites-grid">
              {favoritePhotographers.map((photographer) => (
                <div key={photographer.id} className="userbookings-favorite-card">
                  {/* Verified badge if applicable */}
                  {photographer.isVerified && (
                    <div className="userbookings-favorite-verified-badge">
                      <CheckCircle2 size={14} />
                    </div>
                  )}

                  {/* Online status if applicable */}
                  {photographer.isOnline && (
                    <div className="userbookings-favorite-online-status">
                      <div className="userbookings-favorite-online-dot"></div>
                      Online
                    </div>
                  )}

                  <div
                    className="userbookings-favorite-card-image"
                    onClick={() => viewPhotographerProfile(photographer.photographerId)}
                  >
                    <img
                      src={photographer.profileImage || 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg'}
                      alt={photographer.photographerName}
                      loading="lazy"
                      decoding="async"
                      className="userbookings-favorite-card-img"
                      onError={(e) => {
                        e.target.src = 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg';
                      }}
                    />
                  </div>

                  <div className="userbookings-favorite-card-content">
                    <h3 className="userbookings-favorite-studio-name">
                      {photographer.photographerName}
                      {photographer.isVerified && (
                        <CheckCircle2 size={15} className="userbookings-favorite-verified-icon" />
                      )}
                    </h3>

                    <div className="userbookings-favorite-rating">
                      <div className="userbookings-favorite-stars">
                        {renderStars(parseFloat(photographer.rating) || 4.5)}
                      </div>
                      <span className="userbookings-favorite-rating-text">
                        {(parseFloat(photographer.rating) || 4.5).toFixed(1)} ({parseInt(photographer.reviewsCount) || 0} reviews)
                      </span>
                    </div>

                    <div className="userbookings-favorite-stats">
                      <span className="userbookings-favorite-stat">
                        <Calendar size={13} />
                        {parseInt(photographer.yearsExperience) || 1}+ years
                      </span>
                    </div>

                    <div className="userbookings-favorite-service-type">
                      <span className="userbookings-favorite-service-label">
                        {photographer.specialization || 'Photography'}
                      </span>
                      {photographer.hasStudio && (
                        <span className="userbookings-favorite-studio-badge">
                          <Building2 size={12} /> Studio
                        </span>
                      )}
                    </div>

                    {photographer.specialties && photographer.specialties.length > 0 && (
                      <div className="userbookings-favorite-specialties">
                        {photographer.specialties.slice(0, 2).map((specialty, index) => (
                          <span key={index} className="userbookings-favorite-specialty-tag">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="userbookings-favorite-price">
                      <span className="userbookings-favorite-price-label">
                        Starting From
                      </span>
                      <span className="userbookings-favorite-price-amount">
                        ₹ {photographer.startingPrice || '25,000'}
                      </span>
                    </div>

                    {photographer.responseTime && (
                      <div className="userbookings-favorite-response-time">
                        Responds in {photographer.responseTime}
                      </div>
                    )}

                    <div className="userbookings-favorite-actions">
                      <button 
                        className="userbookings-view-profile-btn"
                        onClick={() => viewPhotographerProfile(photographer.photographerId)}
                      >
                        View Profile
                      </button>
                      <button 
                        className="userbookings-remove-favorite-btn"
                        onClick={() => removeFromFavorites(photographer.photographerId, photographer.photographerName)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="userbookings-no-favorites">
              <Heart size={40} className="userbookings-no-favorites-icon" />
              <h3>No favorites yet</h3>
              <p>Start exploring photographers and add them to your favorites!</p>
              <button 
                className="userbookings-browse-btn"
                onClick={() => navigate('/BrowsePhotographer')}
              >
                Browse Photographers
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default UserFavorites;
